import ExcelJs from 'exceljs';
import { saveAs } from 'file-saver';
import i18n from '@/lang';
import { cloneDeep } from 'lodash-es';
import { truncateDecimalRegex } from '@/views/finance/utils';

/**
 * @type {TTableRow}
 * @desc 模板
 */
export const TMP_OBJ = {
  id: undefined,
  partitionAffiliateId: undefined, // 分区id 文件导入的时候才存在
  feeTypeCode: '', // 费用类型
  startWeight: '', // 重量开始
  endWeight: '', //  重量结束
  price: '', // 区间价格
  overWeightExtraPrice: '', // 超重价格
  overSizeExtraPrice: '', // 超长价格
  overWeight: null, // 超重
  overSize: null // 体积
};

// 抽离cloneDeep
const deepCloneArray = (arr) => cloneDeep(arr);

// #region set设置数据
export const { notSetPartitionFun, setPartitionFun } = (() => {
  /**
   * 没有选择分区
   * @param {any[]} arr 导入的数据
   * @param {TTableRow[]} defaultArr 列表中已经存在的数据
   * @param type 1 普通规则 2 特殊规则
   * @returns {TTableRow[]}
   */
  const notSetPartitionFun = (arr, defaultArr, type) => {
    // 校验 type 参数
    if (type !== 1 && type !== 2) {
      throw new Error('Invalid type parameter');
    }

    // 导入的数据处理
    const list = arr.map(item => {
      if (type === 1) { // 普通规则
        const { id, partitionAffiliateId, startWeight, endWeight, price, feeTypeCode } = item;
        return {
          ...TMP_OBJ,
          id, partitionAffiliateId, startWeight, endWeight, price, feeTypeCode
        };
      } else { // 特殊规则
        const { id, partitionAffiliateId, overWeightExtraPrice, overSizeExtraPrice, overWeight, overSize, feeTypeCode } = item;
        return {
          ...TMP_OBJ,
          type: overSize !== null ? 2 : 1,
          id, partitionAffiliateId, overWeightExtraPrice, overSizeExtraPrice, overWeight, overSize, feeTypeCode
        };
      }
    });

    const defaultList = deepCloneArray(defaultArr);
    console.log(list, '导入的数据', defaultList);

    // 当前为空
    if (defaultList.length === 0) {
      return list;
    }

    const listNotId = []; // 导入的列表不存在id

    list.forEach(item => {
      listNotId.push(item);
    });

    if (type === 1) {
      return [...defaultList, ...listNotId];
    } else {
      if (listNotId.length === 0) {
        return defaultList;
      } else if (listNotId.length > 1) {
        return [...listNotId];
      } else {
        return defaultList.map(item => {
          if (item.type === listNotId[0].type) {
            return {
              ...item,
              ...listNotId[0]
            };
          }
          return item;
        });
      }
    }
  };

  /**
   * 选择了分区
   * @param {any[]} arr 返回报价明细
   * @param {TTableObj} partitionRow 列表中已经存在的数据
   * @returns {TTableObj}
   */
  const setPartitionFun = (arr, partitionRow) => {
    const partitionObj = deepCloneArray(partitionRow);
    console.log(partitionRow, '分区数据', arr, partitionObj);

    // 更新其他分组数据（超重/超尺寸）
    function updateOtherArrItem(target, source, type) {
      target.id = source.id;
      target.type = type;
      target.partitionAffiliateId = source.partitionAffiliateId;
      target.feeTypeCode = source.feeTypeCode || '';

      if (type === 1) {
        target.overWeight = source.overWeight;
        target.overWeightExtraPrice = source.overWeightExtraPrice;
        target.overSize = ''; // 清除非相关字段
        target.overSizeExtraPrice = '';
      } else {
        target.overSize = source.overSize;
        target.overSizeExtraPrice = source.overSizeExtraPrice;
        target.overWeight = ''; // 清除非相关字段
        target.overWeightExtraPrice = '';
      }
    }
    function updateArr(arr, item) {
      arr.push({
        id: item.id,
        feeTypeCode: item.feeTypeCode,
        price: item.price,
        startWeight: item.startWeight,
        endWeight: item.endWeight,
        billingMode: item.billingMode,
        createTime: item.createTime
      });
    }
    // 初始化
    const result = {};

    arr.forEach(item => {
      const partitionKey = String(item.partitionAffiliateId);

      // 初始化分组结构
      if (!result[partitionKey]) {
        result[partitionKey] = {
          arr: [],
          otherArr: [
            { // 类型1模板（超重配置）
              id: '',
              type: 1,
              partitionAffiliateId: '',
              overWeightExtraPrice: '',
              overWeight: '',
              feeTypeCode: ''
            },
            { // 类型2模板（超尺寸配置）
              id: '',
              type: 2,
              partitionAffiliateId: '',
              overSizeExtraPrice: '',
              overSize: '',
              feeTypeCode: ''
            }
          ]
        };
      }

      const group = result[partitionKey];
      const hasOverWeight = item.overWeight !== null;
      const hasOverSize = item.overSize !== null;

      // 优先级：超重 > 超尺寸 > 基础费用
      if (hasOverWeight) {
        updateOtherArrItem(group.otherArr[0], item, 1);
      } else if (hasOverSize) {
        updateOtherArrItem(group.otherArr[1], item, 2);
      } else {
        updateArr(group.arr, item);
      }
    });
    console.log(result, '分区数据');
    return result;
  };

  return {
    notSetPartitionFun, setPartitionFun
  };
})();

// #region 导入导出
export const { downloadTemplate, importTemplate, downloadQuotation, SEPARATOR } = (() => {
  const SEPARATOR = '->'; // 分隔符
  const str1 = i18n.t('receivablePayable.postOfficeDelivery'); // 邮局派送

  // 模板标题
  const headArr = [
    {
      header: i18n.t('basicData.PartitionName'), // 分区
      key: 'partitionAffiliateId',
      width: 30
    },
    {
      header: i18n.t('basicData.ExpenseType') + '*', // 费用类型
      key: 'feeTypeCode',
      width: 30
    },
    {
      header: i18n.t('receivablePayable.weightStart') + '*', // 重量开始(KG) 不包含
      key: 'startWeight',
      width: 30
    },
    {
      header: i18n.t('receivablePayable.weightEndInclusive') + '*', // 重量结束(KG) 包含
      key: 'endWeight',
      width: 30
    },
    {
      header: i18n.t('receivablePayable.priceRange') + '*', // 区间价
      key: 'price',
      width: 30
    },
    {
      header: 'ID', // id 下载报价的时候存在
      key: 'id',
      width: 20
    }
  ];
  // 分区
  const PartitionArr = [
    {
      header: i18n.t('basicData.PartitionName') + '(ID)', // 分区
      key: 'partitionAffiliateId',
      width: 20
    }

  ];

  /**
   * 获取头部，选择与未选择分区，对于索引为0的标题有区别
   *   如果选择了分区则 索引为0 需要加上 *
   *   @param {any[]} subregionList 分区名称
   *   @returns {any[]}
   */
  const getHeadObj = (subregionList) => {
    const arr = deepCloneArray(headArr);
    if (subregionList.length > 0) {
      arr[0].header = arr[0].header + '*';
      return arr;
    }
    return arr;
  };

  /**
   * 生成空白模板
   * @param {any[]} subregionList 所有分区
   * @param {any[]} sheet1RowArr 下载报价的时候才需要传
   * @param feeTypeList 费用类型列表
   */
  const createTemplate = (subregionList, feeTypeList, sheet1RowArr = []) => {
    const wb = new ExcelJs.Workbook();
    const Sheet1 = wb.addWorksheet('Sheet1');
    const Sheet2 = wb.addWorksheet('Sheet2');
    const Sheet3 = wb.addWorksheet('Sheet3');
    console.log(sheet1RowArr, '----------------下载报价');
    // 分区处理
    Sheet1.columns = getHeadObj(subregionList);

    // 下载报价的时候
    if (sheet1RowArr.length) {
      // 过滤掉特殊报价
      const sheetData = sheet1RowArr.filter((item) => !item.type);
      console.log(sheetData, 'sheet1RowArr');
      Sheet1.addRows(sheetData);
    }
    // 分区列表
    Sheet2.columns = deepCloneArray(PartitionArr);
    const Sheet2_data = [];
    if (subregionList && subregionList.length > 0) {
      subregionList.forEach((item) => {
        const { id, partitionIdentifier } = item;
        Sheet2_data.push({
          partitionAffiliateId: `${partitionIdentifier}${SEPARATOR}${id}`
        });
      });
    }
    Sheet2.addRows(Sheet2_data);
    // 费用类型
    Sheet3.columns = [
      {
        header: i18n.t('basicData.ExpenseType') + '(' + i18n.t('ChannelManage.name') + ')', // 费用类型
        key: 'feeTypeName',
        width: 20
      },
      {
        header: i18n.t('basicData.ExpenseType') + '(code)', // 费用类型
        key: 'feeTypeCode',
        width: 20
      }
    ];
    const Sheet3_data = [];
    feeTypeList.map((item) => {
      Sheet3_data.push({
        feeTypeCode: item.code,
        feeTypeName: item.displayName + '->' + item.code
      });
    });
    Sheet3.addRows(Sheet3_data);

    new Array(50000).fill(0).forEach((_, idx) => {
      const row = idx + 2;

      // 分区名称，如果存在则设置
      if (subregionList && subregionList.length > 0) {
        Sheet1.getCell(row, 1).dataValidation = {
          type: 'list',
          formulae: [`=Sheet2!$A$2:$A${Sheet2_data.length + 1}`]
        };
      }

      // 费用类型
      Sheet1.getCell(row, 2).dataValidation = {
        type: 'list',
        formulae: [`=Sheet3!$A$2:$A${Sheet3_data.length + 1}`]
      };
    });

    return wb;
  };

  /**
   * 下载模板
   * @param {any[]} subregionList 所有分区
   * @param feeTypeList 费用类型列表
   */
  const downloadTemplate = async(subregionList, feeTypeList) => {
    const wb = createTemplate(subregionList, feeTypeList);

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/octet-stream'
    });

    // @ts-ignore
    const saveName = `${str1}${i18n.t('basicData.ImportTemplate')}.xlsx`;

    saveAs(blob, saveName);
  };

  /**
   * 导入模板处理
   * @param {any[]} arr 导入的数据
   * @param {any[]} subregionList 分区
   * @returns {TTableRow[]}
   */
  const importTemplate = (arr, subregionList) => {
    console.log('导入的数据', arr, subregionList);

    const subregionID = subregionList.map(item => item.id);

    const headArr = getHeadObj(subregionList);
    console.log(headArr);

    const list = [];
    arr.forEach(item => {
      const obj = {
        ...TMP_OBJ
      };

      Object.entries(item).map(row => {
        const [key, value] = row;
        console.log(key, value, item, subregionID);
        // 选择分区
        if (subregionID.length !== 0) {
          // 分区
          if (key === headArr[0].header) {
            const arr = value.split(SEPARATOR);
            console.log(arr, 'arr');
            obj[headArr[0].key] = subregionList.find(item => item.id === Number(arr[1])).id;
          }
        }
        // 费用
        if (key === headArr[1].header) {
          const arr = (value || '').split(SEPARATOR);
          console.log(arr, 'arr');
          obj[headArr[1].key] = arr[1];
        }
        // 开始重量
        if (key === headArr[2].header) {
          obj[headArr[2].key] = value >= 0 ? truncateDecimalRegex(value, 2) : undefined;
        }
        // 结束重量
        if (key === headArr[3].header) {
          obj[headArr[3].key] = value > 0 ? truncateDecimalRegex(value, 2) : undefined;
        }
        // 区间价
        if (key === headArr[4].header) {
          obj[headArr[4].key] = value >= 0 ? truncateDecimalRegex(value, 2) : undefined;
        }
        // id
        if (headArr[5] && key === headArr[5].header) {
          obj[headArr[5].key] = value > 0 ? value : undefined;
        }

        return obj;
      });

      list.push(obj);
    });
    console.log(list);
    return list;
  };

  // 下载报价
  const downloadQuotation = async(list, subregionList, feeTypeList) => {
    const wb = createTemplate(subregionList, feeTypeList, list);

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/octet-stream'
    });

    const saveName = `${i18n.t('receivablePayable.postOfficeDelivery')}${i18n.t('basicData.QuoteDetails')}.xlsx`;

    saveAs(blob, saveName);
  };

  return {
    downloadTemplate,
    importTemplate,
    downloadQuotation,
    SEPARATOR
  };
})();
